import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import PanelTabs, { StaffHead } from '../components/ui/PanelTabs';
import { canModerateLibrary } from '../constants/roles';
import { DASHBOARD_TABS } from '../constants/botNav';
import BotLicenceTab from './admin/BotLicenceTab';
import RoutesTab from './user/RoutesTab';
import LibraryTab from './user/LibraryTab';
import ReviewTab from './user/ReviewTab';
import FarmingTab from './user/FarmingTab';

/**
 * A bot oldalának Dashboardja (bot.mortisengine.com) — a régi Codex-admin „Bot” része (BotSection, 2026-10-06), saját
 * oldalon: fülenként saját cím (/licence, /routes, /library, /review, /farming), hogy a vissza-gomb és a link működjön.
 * A komponensek és a végpontok ugyanazok, mint az admin paneljében voltak.
 */

/** A Library review várakozó beküldései (a fül maga is frissíti, amikor betölt vagy dönt). */
function useReviewPending(role) {
  const [pending, setPending] = useState(0);
  const onCounts = useCallback((c) => setPending(c?.pending || 0), []);
  useEffect(() => {
    if (!canModerateLibrary(role)) return;
    api.get('/library/review/counts').then(r => setPending(r.data.pending || 0)).catch(() => {});
  }, [role]);
  return [pending, onCounts];
}

const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', tester: 'Tester' };

export default function DashboardPage() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [reviewPending, onReviewCounts] = useReviewPending(user?.role);

  const moderator = canModerateLibrary(user?.role);
  const tabs = DASHBOARD_TABS
    .filter(t => !t.moderators || moderator)
    .map(t => (t.id === 'review' ? { ...t, count: reviewPending } : t));
  const active = tabs.find(t => pathname === t.path || pathname.startsWith(`${t.path}/`));
  if (!active) return <Navigate to="/licence" replace />;

  return (
    <div className="wrap pb-20 sm:pb-28">
      <StaffHead
        eyebrow={ROLE_LABEL[user?.role] || 'MortisEngine'}
        title="Dashboard"
        subtitle={<>Signed in as <span className="text-gray-100">{user?.username}</span></>}
      />
      <PanelTabs
        className="mt-[18px] mb-8"
        active={active.id}
        onChange={(id) => navigate(tabs.find(t => t.id === id).path)}
        tabs={tabs}
      />
      {active.id === 'licence' && <BotLicenceTab />}
      {active.id === 'routes' && <RoutesTab />}
      {active.id === 'library' && <LibraryTab />}
      {active.id === 'review' && <ReviewTab role={user?.role} onCounts={onReviewCounts} />}
      {active.id === 'farming' && <FarmingTab />}
    </div>
  );
}
